/**
 * Conversations Client
 * Conversation and message operations for operator dashboard
 */

import type {
  OperatorConversation,
  OperatorMessage,
  ConversationFilters,
  ConversationListResponse,
  ConversationDetailResponse,
} from './types';
import { apiFetch, handleApiError, logApiCall } from './base-client';

// =============================================================================
// Response Mapping Helpers
// =============================================================================

/**
 * Maps a raw API conversation object (nested customer/business) to the flat
 * OperatorConversation shape expected by the frontend.
 */
function mapApiConversation(raw: Record<string, unknown>): OperatorConversation {
  const customer = (raw.customer ?? {}) as Record<string, unknown>;
  const business = (raw.business ?? {}) as Record<string, unknown>;

  return {
    id: raw.id as string,
    businessId: raw.businessId as string,
    customerId: raw.customerId as string,
    customerPhone: (customer.phoneNumberE164 as string) ?? '',
    customerName: (customer.name as string) ?? null,
    currentStep: (raw.currentStep as string) ?? '',
    lastMessageAt: (raw.lastMessageAt as string) ?? '',
    messageCount: (raw.messageCount as number) ?? 0,
    createdAt: (raw.createdAt as string) ?? '',
    businessName: (business.name as string) ?? undefined,
  };
}

// =============================================================================
// Conversation Operations
// =============================================================================

/**
 * Get paginated list of conversations with role-based PII masking.
 * Backend applies PII masking based on operator role.
 *
 * @param filters - Optional filters for the query
 * @returns Paginated conversation list
 *
 * @example
 * ```ts
 * const { conversations, total } = await getOperatorConversations({
 *   businessId: 'biz-001',
 *   page: 1,
 *   limit: 20
 * });
 * ```
 */
export async function getOperatorConversations(
  filters?: ConversationFilters
): Promise<ConversationListResponse> {
  const startTime = Date.now();
  const endpoint = '/api/operator/conversations';
  const defaultResponse: ConversationListResponse = {
    conversations: [],
    total: 0,
    page: 1,
    limit: 20,
  };

  try {
    const params = new URLSearchParams();

    if (filters?.businessId) params.append('businessId', filters.businessId);
    if (filters?.phoneNumber) params.append('search', filters.phoneNumber);
    if (filters?.state) params.append('view', filters.state);
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    const limit = filters?.limit || 20;
    params.append('limit', String(limit));

    const queryString = params.toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;

    const response = await apiFetch(url);

    logApiCall(endpoint, 'GET', Date.now() - startTime, response.status);

    if (!response.ok) {
      if (response.status === 403) {
        return defaultResponse;
      }
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();

    // The API returns { success, conversations, nextCursor, stats }
    // Map nested objects to flat OperatorConversation shape
    const rawConversations: Record<string, unknown>[] = data.conversations ?? [];
    const conversations = rawConversations.map(mapApiConversation);

    const stats = (data.stats ?? {}) as Record<string, number>;

    return {
      conversations,
      total: stats.total ?? conversations.length,
      page: filters?.page ?? 1,
      limit,
    };
  } catch (error) {
    handleApiError(error, 'getOperatorConversations');
    return defaultResponse;
  }
}

/**
 * Get single conversation with its messages
 *
 * @param conversationId - The conversation ID to retrieve
 * @returns Conversation with messages or null if not found/unauthorized
 *
 * @example
 * ```ts
 * const detail = await getOperatorConversation('conv-001');
 * if (detail) {
 *   console.log(`${detail.messages.length} messages`);
 * }
 * ```
 */
export async function getOperatorConversation(
  conversationId: string
): Promise<ConversationDetailResponse | null> {
  const startTime = Date.now();
  const endpoint = `/api/operator/conversations/${conversationId}`;

  try {
    const response = await apiFetch(endpoint);

    logApiCall(endpoint, 'GET', Date.now() - startTime, response.status);

    if (!response.ok) {
      if (response.status === 403 || response.status === 404) return null;
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();

    // Handle API response format: { success, conversation, messages }
    if (data.success && data.conversation) {
      return {
        conversation: mapApiConversation(data.conversation),
        messages: (data.messages ?? []) as OperatorMessage[],
      };
    }

    return null;
  } catch (error) {
    handleApiError(error, 'getOperatorConversation', { conversationId });
    return null;
  }
}

/**
 * Get conversations for a specific business
 *
 * @param businessId - Business ID to filter by
 * @param page - Page number
 * @param limit - Items per page
 * @returns Paginated conversation list for the business
 */
export async function getBusinessConversations(
  businessId: string,
  page = 1,
  limit = 20
): Promise<ConversationListResponse> {
  return getOperatorConversations({ businessId, page, limit });
}

/**
 * Search conversations by phone number
 *
 * @param phoneNumber - Phone number to search for (partial match)
 * @param businessId - Optional business ID filter
 * @returns Matching conversations
 */
export async function searchConversationsByPhone(
  phoneNumber: string,
  businessId?: string
): Promise<ConversationListResponse> {
  return getOperatorConversations({ phoneNumber, businessId });
}

/**
 * Get conversations by state
 *
 * @param state - Conversation state to filter by
 * @param businessId - Optional business ID filter
 * @returns Conversations in the specified state
 */
export async function getConversationsByState(
  state: string,
  businessId?: string
): Promise<ConversationListResponse> {
  return getOperatorConversations({ state, businessId });
}

/**
 * Get conversations within a date range
 *
 * @param dateFrom - Start date (ISO string)
 * @param dateTo - End date (ISO string)
 * @param businessId - Optional business ID filter
 * @returns Conversations within the date range
 */
export async function getConversationsByDateRange(
  dateFrom: string,
  dateTo: string,
  businessId?: string
): Promise<ConversationListResponse> {
  return getOperatorConversations({ dateFrom, dateTo, businessId });
}
